/* eslint-disable functional/no-throw-statements */
import { InjectionToken } from '@angular/core';

export type AngularRuntimeConfig = Readonly<{
  apiBaseUrl: string;
  /**
  Origin-relative base URL per generated API client, keyed by the API name
  used in `tools/api-registry.ts` (for example `masterdata`, `inventory`).

  Supplied by the Shell as the `api-base-urls` attribute so no environment
  specific host is compiled into the generated client. Resolved at request
  time by `apiBaseUrlInterceptor`.
  */
  apiBaseUrls: Readonly<Record<string, string>>;
  // Legacy Shell configuration; accepted but unused by the local catalogue loader.
  i18nBaseUrl: string;
}>;

export const RUNTIME_CONFIG = new InjectionToken<AngularRuntimeConfig>('RUNTIME_CONFIG');

/**
Keycloak connection details for the standalone build's own login.

Standalone-only: in federated mode the Shell owns authentication entirely
(see `src/shell/auth/auth.service.ts`), so this never appears in
`AngularRuntimeConfig`.
*/
export type KeycloakRuntimeConfig = Readonly<{
  clientId: string;
  realm: string;
  url: string;
}>;

/**
Runtime config for the standalone build — everything `AngularRuntimeConfig`
has, plus the i18n catalogue revision (Shell-supplied as the
`catalogue-revision` attribute in federated mode; standalone has no
attribute to read, so it travels in this same fetched config instead) and
the standalone build's own Keycloak connection.
*/
export type StandaloneRuntimeConfig = AngularRuntimeConfig &
  Readonly<{
    catalogueRevision: string;
    keycloak: KeycloakRuntimeConfig;
  }>;

export const STANDALONE_RUNTIME_CONFIG = new InjectionToken<StandaloneRuntimeConfig>(
  'STANDALONE_RUNTIME_CONFIG',
);

/**
Validates and normalizes an origin-relative base URL.
@param value - Candidate origin-relative URL to validate.
@param fieldName - Configuration field name used in validation errors.
@returns The normalized origin-relative path without trailing slashes.
@throws {Error} If the value is absolute, contains a query or hash, or refers to the origin root.
*/
export function normalizeOriginRelativeBaseUrl(value: string, fieldName: string): string {
  const normalizedValue = value.trim();

  if (!normalizedValue.startsWith('/') || normalizedValue.startsWith('//')) {
    throw new Error(`${fieldName} must be an origin-relative path beginning with "/".`);
  }

  const parsedUrl = new URL(normalizedValue, location.origin);

  if (parsedUrl.origin !== location.origin || parsedUrl.search || parsedUrl.hash) {
    throw new Error(`${fieldName} must contain only an origin-relative path.`);
  }

  const normalizedPath = removeTrailingSlashes(parsedUrl.pathname);

  if (!normalizedPath) {
    throw new Error(`${fieldName} must not be the origin root.`);
  }

  return normalizedPath;
}

/**
Parses and validates the Shell-supplied `api-base-urls` attribute.

The attribute carries a JSON object mapping generated API names to
origin-relative base URLs, e.g. `{"masterdata":"/keyoris/ima"}`. Every value
is validated with {@link normalizeOriginRelativeBaseUrl}; nothing is coerced
and nothing is partially accepted.
@param value - Raw attribute value, or null when the attribute is absent.
@param fieldName - Attribute name used in validation errors.
@param knownApis - API names this build generates clients for.
@returns A frozen map of API name to normalized origin-relative base URL.
@throws {Error} If the value is absent, not a JSON object, or holds an invalid base URL.
*/
export function parseApiBaseUrls(
  value: null | string,
  fieldName: string,
  knownApis: readonly string[],
): Readonly<Record<string, string>> {
  if (!value?.trim()) {
    throw new Error(`${fieldName} is required and must not be empty.`);
  }

  const parsed = parseJsonObject(value, fieldName);
  const entries = Object.entries(parsed);

  if (entries.length === 0) {
    throw new Error(`${fieldName} must configure at least one API.`);
  }

  const normalized = entries.map(([apiName, baseUrl]): readonly [string, string] => {
    if (typeof baseUrl !== 'string') {
      throw new TypeError(`${fieldName}.${apiName} must be a string.`);
    }

    if (!knownApis.includes(apiName)) {
      // Forward compatible: lets the Shell roll out a new API ahead of this MFE.
      console.warn(`${fieldName} contains unknown API "${apiName}"; ignoring it.`);
    }

    return [apiName, normalizeOriginRelativeBaseUrl(baseUrl, `${fieldName}.${apiName}`)];
  });

  return Object.freeze(Object.fromEntries(normalized));
}

/**
Validates and normalizes the standalone build's Keycloak configuration.

Unlike the API base URLs, `url` is a real absolute origin (Keycloak lives
on its own host), not an origin-relative path — so it gets its own
validation instead of {@link normalizeOriginRelativeBaseUrl}.
@param value - Candidate `keycloak` field from the parsed runtime config.
@param fieldName - Field name used in validation errors.
@returns The normalized Keycloak configuration.
@throws {Error} If the value is missing, malformed, or any field is invalid.
*/
export function parseKeycloakConfig(value: unknown, fieldName: string): KeycloakRuntimeConfig {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new TypeError(`${fieldName} must be a JSON object with "url", "realm", "clientId".`);
  }

  const record = value as Record<string, unknown>;

  return Object.freeze({
    url: normalizeKeycloakUrl(record['url'], `${fieldName}.url`),
    realm: readNonEmptyString(record['realm'], `${fieldName}.realm`),
    clientId: readNonEmptyString(record['clientId'], `${fieldName}.clientId`),
  });
}

/**
Validates the Keycloak server's absolute base URL.
@param value - Candidate URL value.
@param fieldName - Field name used in validation errors.
@returns The URL with any trailing slash removed.
@throws {TypeError} If the value is not a string. {@link Error} If it is not an absolute http(s) URL.
*/
function normalizeKeycloakUrl(value: unknown, fieldName: string): string {
  const url = readNonEmptyString(value, fieldName);
  const parsed = parseAbsoluteUrl(url, fieldName);

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    throw new Error(`${fieldName} must use http or https.`);
  }

  return removeTrailingSlashes(url) || url;
}

/**
Parses an absolute URL, converting a syntax error into a configuration error.
@param url - Candidate URL text.
@param fieldName - Field name used in validation errors.
@returns The parsed URL.
@throws {Error} If the text is not a valid absolute URL.
*/
function parseAbsoluteUrl(url: string, fieldName: string): URL {
  try {
    return new URL(url);
  } catch {
    throw new Error(`${fieldName} must be an absolute URL.`);
  }
}

/**
Parses an attribute value that must contain a JSON object.
@param value - Raw attribute value.
@param fieldName - Attribute name used in validation errors.
@returns The parsed object.
@throws {Error} If the value is not valid JSON, or is not a plain object.
*/
function parseJsonObject(value: string, fieldName: string): Record<string, unknown> {
  const parsed: unknown = tryParseJson(value, fieldName);

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new TypeError(`${fieldName} must be a JSON object mapping API names to base URLs.`);
  }

  return parsed as Record<string, unknown>;
}

/**
Reads a required, non-empty string field.
@param value - Candidate value.
@param fieldName - Field name used in validation errors.
@returns The trimmed string value.
@throws {TypeError} If the value is not a non-empty string.
*/
function readNonEmptyString(value: unknown, fieldName: string): string {
  if (typeof value !== 'string' || !value.trim()) {
    throw new TypeError(`${fieldName} must be a non-empty string.`);
  }

  return value.trim();
}

/**
Parses JSON, converting a syntax error into a configuration error.
@param value - Raw JSON text.
@param fieldName - Attribute name used in validation errors.
@returns The parsed value.
@throws {Error} If the text is not valid JSON.
*/
function tryParseJson(value: string, fieldName: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    throw new Error(`${fieldName} must be a valid JSON object.`);
  }
}

/**
Removes trailing slash characters from a path.
@param path - Path to normalize.
@returns The path without trailing slash characters.
*/
const removeTrailingSlashes = (path: string): string => {
  return path === '/' ? '' : path.replace(/([^/])\/+$/u, '$1');
};
